/* ============================================================================
   Lab Effects — All Reviews page

   Every review is server-rendered into the grid by sections/le-all-reviews.liquid.
   This file never fetches or builds review content — it hides, reorders and
   reveals nodes that are already in the HTML. That matters twice over: the text
   stays crawlable, and FILTERING REACHES THE WHOLE SET rather than only the
   cards "Load more" has revealed so far. Load more raises a visible ceiling; it
   is not a data boundary.

   Cards, modal, votes and flags come from le-pdp-reviews.js via window.LEReviews
   so the product page stays the single source of truth for how a review looks and
   behaves. Only the page-level controls differ (three scopes, masonry, load-more,
   rating breakdown), so only those live here.

   Votes are keyed by review id and stored server-side, so a vote cast on a
   product page is already reflected here, and vice versa.
============================================================================ */
(function () {
  "use strict";

  var LE = window.LEReviews;
  if (!LE) return; // le-pdp-reviews.js is a hard dependency; the section loads it first.

  function init(root) {
    if (root.dataset.ready === "1") return;
    root.dataset.ready = "1";

    var worker = (root.getAttribute("data-worker") || "").replace(/\/+$/, "");
    var PAGE = parseInt(root.getAttribute("data-page-size"), 10) || 12;

    var grid = root.querySelector("[data-grid]");
    if (!grid) return;

    var els = {
      scopes: root.querySelectorAll("[data-scope]"),
      filters: root.querySelector("[data-filters]"),
      breakdown: root.querySelector("[data-breakdown]"),
      sumAvg: root.querySelector("[data-sum-avg]"),
      sumStars: root.querySelector("[data-sum-stars]"),
      sumTotal: root.querySelector("[data-sum-total]"),
      sumLabel: root.querySelector("[data-sum-label]"),
      shownN: root.querySelector("[data-shown-n]"),
      filteredN: root.querySelector("[data-filtered-n]"),
      status: root.querySelector("[data-status]"),
      moreWrap: root.querySelector("[data-more-wrap]"),
      more: root.querySelector("[data-more]")
    };

    var ALL = [].slice.call(grid.querySelectorAll("[data-card]"));
    var state = { scope: "all", filter: "all", sort: "recent", visible: PAGE };

    /* The filters accordion is closed on mobile and forced open on desktop. The
       `open` attribute is not settable from CSS, so it is driven here — and kept
       in sync on resize, since a rotation must not leave a desktop layout with a
       collapsed filter row. Once a person opens it on mobile, leave it alone. */
    var fdrop = root.querySelector("[data-fdrop]");
    if (fdrop) {
      var mq = window.matchMedia("(max-width: 640px)");
      var touched = false;
      fdrop.addEventListener("toggle", function () { if (mq.matches) touched = true; });
      var syncDrop = function () {
        if (!mq.matches) { fdrop.open = true; touched = false; }
        else if (!touched) { fdrop.open = false; }
      };
      syncDrop();
      (mq.addEventListener ? mq.addEventListener.bind(mq, "change") : mq.addListener.bind(mq))(syncDrop);
    }

    LE.paintFlags(root);

    root.querySelectorAll("[data-open]").forEach(function (b) {
      b.addEventListener("click", function () { LE.openModal(b.closest("[data-card]")); });
    });

    // Re-sort once vote counts land — "Most helpful" would otherwise be ordering
    // the zeroes that were on screen before the Worker replied.
    LE.initVotes(root, worker, function () { if (state.sort === "helpful") render(); });

    /* ---- scope: which cards this tab is about, before any filter ---- */
    function inScope(c) {
      if (state.scope === "all") return true;
      return c.getAttribute("data-type") === state.scope;
    }
    function scopeCards() { return ALL.filter(inScope); }

    /* ---- summary + breakdown: always describe the CURRENT scope ---- */
    function renderSummary() {
      var list = scopeCards();
      var sum = 0, verified = 0, buckets = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
      list.forEach(function (c) {
        var r = +c.getAttribute("data-rating") || 0;
        sum += r;
        buckets[r] = (buckets[r] || 0) + 1;
        if (c.querySelector(".le-rv-verified")) verified++;
      });
      var avg = list.length ? Math.round((sum / list.length) * 10) / 10 : 0;

      els.sumAvg.textContent = avg.toFixed(1);
      els.sumStars.innerHTML = LE.starsHTML(avg, 24);
      // Count every review in scope, not just verified-buyer ones. Store reviews
      // carry no verified flag, so counting those alone rendered "0 reviews"
      // under a 4.6 average.
      els.sumTotal.textContent = list.length;
      els.sumLabel.textContent = verified === list.length && list.length
        ? (list.length === 1 ? "verified review" : "verified reviews")
        : (list.length === 1 ? "review" : "reviews");

      els.breakdown.innerHTML = "";
      [5, 4, 3, 2, 1].forEach(function (s) {
        var n = buckets[s] || 0;
        var pct = list.length ? Math.round((n / list.length) * 100) : 0;
        var on = state.filter === "s" + s;
        var row = LE.el("button", {
          class: "le-allrv-brow" + (on ? " on" : ""), type: "button",
          disabled: n === 0 ? "" : null,
          "aria-pressed": on ? "true" : "false",
          "aria-label": n + " " + s + "-star reviews, " + pct + " percent",
          // Clicking a bar filters to that rating; clicking the active one clears.
          onClick: function () { setFilter(on ? "all" : "s" + s); }
        }, [
          LE.el("span", { class: "le-allrv-brow__star", html: s + LE.star(true, 14) }),
          LE.el("span", { class: "le-allrv-brow__bar" }, [
            LE.el("span", { class: "le-allrv-brow__fill", style: "width:" + pct + "%" })
          ]),
          LE.el("span", { class: "le-allrv-brow__pct", text: pct + "%" })
        ]);
        els.breakdown.appendChild(row);
      });
    }

    /* ---- filter chips + sort (same controls as the product page) ---- */
    function renderFilters() {
      els.filters.innerHTML = "";
      var list = scopeCards();
      if (!list.length) return;

      function countOf(r) { return list.filter(function (c) { return +c.getAttribute("data-rating") === r; }).length; }
      var photoCount = list.filter(function (c) { return c.getAttribute("data-photo") === "1"; }).length;

      function chip(key, label, withStar, count) {
        var on = state.filter === key;
        return LE.el("button", {
          class: "le-rv-chip" + (on ? " on" : ""), type: "button",
          "aria-pressed": on ? "true" : "false",
          disabled: count === 0 ? "" : null,
          onClick: function () { setFilter(key); }
        }, [
          withStar ? LE.el("span", { class: "le-rv-stars", html: LE.star(true, 15) }) : null,
          label,
          count != null ? LE.el("span", { class: "le-rv-chip-n", text: "(" + count + ")" }) : null
        ]);
      }

      els.filters.appendChild(chip("all", "All", false, list.length));
      [5, 4, 3, 2, 1].forEach(function (n) { els.filters.appendChild(chip("s" + n, String(n), true, countOf(n))); });
      if (photoCount) els.filters.appendChild(chip("photos", "With photos", false, photoCount));

      // Only offer sorts that can do something here.
      var opts = [["recent", "Most recent"], ["highest", "Highest rated"], ["lowest", "Lowest rated"]];
      if (worker) opts.push(["helpful", "Most helpful"]);
      if (photoCount) opts.push(["photos", "Photos first"]);
      if (!opts.some(function (o) { return o[0] === state.sort; })) state.sort = "recent";

      var sel = LE.el("select", {
        class: "le-rv-sort", "aria-label": "Sort reviews",
        onChange: function (e) { state.sort = e.target.value; state.visible = PAGE; render(); syncDropLabel(); }
      });
      opts.forEach(function (o) {
        var op = LE.el("option", { value: o[0], text: o[1] });
        if (o[0] === state.sort) op.selected = true;
        sel.appendChild(op);
      });
      els.filters.appendChild(LE.el("div", { class: "le-rv-sortwrap" }, [sel]));
    }

    // The collapsed mobile pill has to say what is on, or a filter set earlier is
    // invisible once the accordion closes.
    function syncDropLabel() {
      var lbl = root.querySelector("[data-fdrop-label]");
      if (!lbl) return;
      var bits = [];
      if (state.filter === "photos") bits.push("With photos");
      else if (state.filter[0] === "s") bits.push(state.filter.slice(1) + " star");
      var sortLbl = { recent: "Most recent", highest: "Highest rated", lowest: "Lowest rated", helpful: "Most helpful", photos: "Photos first" }[state.sort];
      if (state.sort !== "recent" && sortLbl) bits.push(sortLbl);
      lbl.textContent = bits.length ? bits.join(" · ") : "Filters";
    }

    function setFilter(key) {
      state.filter = key;
      state.visible = PAGE; // a new filter is a new list; start it at the top
      render();
      renderFilters();
      renderSummary();
      syncDropLabel();
    }

    function votesOf(c) {
      var up = c.querySelector("[data-up]"), down = c.querySelector("[data-down]");
      return (up ? +up.textContent || 0 : 0) - (down ? +down.textContent || 0 : 0);
    }

    /* ---- the one place visibility is decided ---- */
    function render() {
      // Match against every card in scope — never against the visible slice.
      var matching = scopeCards().filter(function (c) {
        if (state.filter[0] === "s") return +c.getAttribute("data-rating") === +state.filter.slice(1);
        if (state.filter === "photos") return c.getAttribute("data-photo") === "1";
        return true;
      });

      matching.sort(function (a, b) {
        var ra = +a.getAttribute("data-rating"), rb = +b.getAttribute("data-rating");
        var ta = +a.getAttribute("data-ts"), tb = +b.getAttribute("data-ts");
        if (state.sort === "highest") return rb - ra || tb - ta;
        if (state.sort === "lowest") return ra - rb || tb - ta;
        if (state.sort === "helpful") return votesOf(b) - votesOf(a) || tb - ta;
        if (state.sort === "photos") return (b.getAttribute("data-photo") - a.getAttribute("data-photo")) || tb - ta;
        return tb - ta;
      });

      ALL.forEach(function (c) { c.hidden = true; });
      matching.slice(0, state.visible).forEach(function (c) {
        c.hidden = false;
        grid.appendChild(c); // reflow into sorted order
      });

      var shown = Math.min(state.visible, matching.length);
      els.shownN.textContent = shown;
      els.filteredN.textContent = matching.length;

      if (!matching.length) {
        var msg = "No reviews match this filter yet.";
        if (state.filter === "photos") msg = "No reviews with photos yet.";
        else if (state.filter[0] === "s") msg = "No " + state.filter.slice(1) + "-star reviews yet.";
        else if (state.scope === "store") msg = "No store reviews yet.";
        else if (state.scope === "product") msg = "No product reviews yet.";
        els.status.textContent = msg;
        els.status.hidden = false;
      } else {
        els.status.hidden = true;
      }
      els.moreWrap.hidden = matching.length <= state.visible;
    }

    els.scopes.forEach(function (t) {
      t.addEventListener("click", function () {
        state.scope = t.getAttribute("data-scope");
        state.filter = "all";
        state.visible = PAGE;
        els.scopes.forEach(function (o) {
          var on = o === t;
          o.classList.toggle("on", on);
          o.setAttribute("aria-selected", on ? "true" : "false");
        });
        renderSummary(); renderFilters(); render(); syncDropLabel();
      });
    });

    els.more.addEventListener("click", function () {
      var before = state.visible;
      state.visible += PAGE;
      render();
      // Move focus to the first newly revealed card so keyboard and screen-reader
      // users land on the new content instead of a button that may have vanished.
      var next = grid.querySelectorAll("[data-card]:not([hidden])")[before];
      if (next) {
        next.setAttribute("tabindex", "-1");
        next.focus({ preventScroll: true });
      }
    });

    renderSummary(); renderFilters(); render(); syncDropLabel();
  }

  function boot(scope) { (scope || document).querySelectorAll("[data-le-all-reviews]").forEach(init); }
  if (document.readyState !== "loading") boot();
  else document.addEventListener("DOMContentLoaded", function () { boot(); });
  document.addEventListener("shopify:section:load", function (e) { boot(e.target); });
})();
